import createReducer from "../../reducers/createReducer"
import {
    GET_REPORTS_SUCCESS,
    GET_REPORTS_LOADING,
    GET_REPORTS_ERROR,
    DOWNLOAD_REPORTS_LIST_START,
    DOWNLOAD_REPORTS_LIST_COMPLETE,
    CLIENT_ANALYSIS_LOADING,
    CLIENT_ANALYSIS_SUCCESS,
    CLIENT_ANALYSIS_ERROR,
    REVENUE_ANALYSIS_LOADING,
    REVENUE_ANALYSIS_SUCCESS,
    REVENUE_ANALYSIS_ERROR,
    TRANSACTION_ANALYSIS_LOADING,
    TRANSACTION_ANALYSIS_SUCCESS,
    TRANSACTION_ANALYSIS_ERROR,
    USER_STATS_LOADING,
    USER_STATS_SUCCESS,
    USER_STATS_ERROR,
    SALARY_REVENUE_ANALYSIS_LOADING,
    SALARY_REVENUE_ANALYSIS_SUCCESS,
    SALARY_REVENUE_ANALYSIS_ERROR,
    COMPANY_ANALYSIS_LOADING,
    COMPANY_ANALYSIS_SUCCESS,
    COMPANY_ANALYSIS_ERROR,
    RESET_ANALYSIS_ERROR,
} from "./types"

// Empty per-report bucket. Each analysis stores its own data/loading/error
// so switching reports doesn't clobber the previous one's cached rows.
// `fieldErrors` holds the { field: [messages] } 400 payload; `error` holds
// the top-level `detail` (or a generic fallback) so components can decide
// which surface to render each in.
const emptyAnalysisSlice = {
    data: null,
    loading: false,
    error: null,
    fieldErrors: null,
};


let initialState = {
    loading: false,
    reportsList: [],
    success: null,
    error: null,
    filters: {
        employee_id: "",
        start_date: null,
        end_date: null,
    },
    isDownloadStarted: false,

    // Per-report analysis slices. Naming mirrors the endpoint segments
    // (clientanalysis → client, transactionanalysis → transaction) so
    // selectors + components use one obvious key.
    clientAnalysis: { ...emptyAnalysisSlice },
    revenueAnalysis: { ...emptyAnalysisSlice },
    transactionAnalysis: { ...emptyAnalysisSlice },
    userStats: { ...emptyAnalysisSlice },
    salaryRevenueAnalysis: { ...emptyAnalysisSlice },
    companyAnalysis: { ...emptyAnalysisSlice },
};

// Maps the RESET_ANALYSIS_ERROR `analysis` arg to the state key. Kept as
// an explicit table so a typo throws instead of silently clearing nothing.
const ANALYSIS_KEYS = {
    client: "clientAnalysis",
    revenue: "revenueAnalysis",
    transaction: "transactionAnalysis",
    user_stats: "userStats",
    salary_revenue: "salaryRevenueAnalysis",
    company: "companyAnalysis",
};

// Small helper to keep the six per-report reducer handlers uniform. Each
// handler set is identical apart from the state key it writes to.
const analysisHandlers = (stateKey) => ({
    loading: (state) => Object.assign({}, state, {
        [stateKey]: Object.assign({}, state[stateKey], {
            loading: true,
            error: null,
            fieldErrors: null,
        }),
    }),
    success: (state, action) => Object.assign({}, state, {
        [stateKey]: Object.assign({}, state[stateKey], {
            loading: false,
            data: action.payload,
            error: null,
            fieldErrors: null,
        }),
    }),
    error: (state, action) => Object.assign({}, state, {
        [stateKey]: Object.assign({}, state[stateKey], {
            loading: false,
            error: action.payload?.detail || null,
            fieldErrors: action.payload?.fieldErrors || null,
        }),
    }),
});

const client = analysisHandlers("clientAnalysis");
const revenue = analysisHandlers("revenueAnalysis");
const transaction = analysisHandlers("transactionAnalysis");
const user = analysisHandlers("userStats");
const salary = analysisHandlers("salaryRevenueAnalysis");
const company = analysisHandlers("companyAnalysis");


export const reportsReducer = createReducer(initialState, {
    [GET_REPORTS_LOADING](state, action) {
        return Object.assign({}, state, {
            loading: true,
            reportsList: [],
            success: null,
            error: null,
            filters: action.payload.filters,

        });
    },
    [GET_REPORTS_SUCCESS](state, action) {
        return Object.assign({}, state, {
            loading: false,
            reportsList: action.payload,
            error: null,
        });
    },
    [GET_REPORTS_ERROR](state, action) {
        return Object.assign({}, state, {
            loading:false,
            reportsList: [],
            error: action.payload,
        });
    },
    [DOWNLOAD_REPORTS_LIST_START](state) {
        return Object.assign({}, state, {
            isDownloadStarted: true,
        });
    },
    [DOWNLOAD_REPORTS_LIST_COMPLETE](state) {
        return Object.assign({}, state, {
            isDownloadStarted: false,
        });
    },

    [CLIENT_ANALYSIS_LOADING]: client.loading,
    [CLIENT_ANALYSIS_SUCCESS]: client.success,
    [CLIENT_ANALYSIS_ERROR]: client.error,

    [REVENUE_ANALYSIS_LOADING]: revenue.loading,
    [REVENUE_ANALYSIS_SUCCESS]: revenue.success,
    [REVENUE_ANALYSIS_ERROR]: revenue.error,

    [TRANSACTION_ANALYSIS_LOADING]: transaction.loading,
    [TRANSACTION_ANALYSIS_SUCCESS]: transaction.success,
    [TRANSACTION_ANALYSIS_ERROR]: transaction.error,

    [USER_STATS_LOADING]: user.loading,
    [USER_STATS_SUCCESS]: user.success,
    [USER_STATS_ERROR]: user.error,

    [SALARY_REVENUE_ANALYSIS_LOADING]: salary.loading,
    [SALARY_REVENUE_ANALYSIS_SUCCESS]: salary.success,
    [SALARY_REVENUE_ANALYSIS_ERROR]: salary.error,

    [COMPANY_ANALYSIS_LOADING]: company.loading,
    [COMPANY_ANALYSIS_SUCCESS]: company.success,
    [COMPANY_ANALYSIS_ERROR]: company.error,

    [RESET_ANALYSIS_ERROR](state, action) {
        const stateKey = ANALYSIS_KEYS[action.analysis];
        if (!stateKey) return state;
        return Object.assign({}, state, {
            [stateKey]: Object.assign({}, state[stateKey], {
                error: null,
                fieldErrors: null,
            }),
        });
    },

});